import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { danas } from "@/lib/dan";
import {
  hasPermission,
  needsComment,
  needsPhoto,
  outcomeLabels,
  workflow,
  type Crew,
  type OutcomeStatus,
  type SiteTask,
  type TaskInput,
  type TaskOutcome,
  type TaskOutcomeSummary,
} from "@/lib/workflow";
import {
  cardClass,
  controlClass,
  errorText,
  Feedback,
  PeriodLockNotice,
  useResource,
  useWriteWindow,
} from "./shared";

export function TaskPanel({ siteId }: { siteId: string }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [crew, setCrew] = useState("");
  const [status, setStatus] = useState("aktivan");
  const [editing, setEditing] = useState<SiteTask | "new" | null>(null);
  const [chosen, setChosen] = useState(params.get("takt") ?? "");
  const resource = useResource(
    siteId,
    useCallback(
      async (signal) => {
        const [tasks, crews] = await Promise.all([
          workflow.tasks(siteId, signal),
          workflow.crews(siteId, signal),
        ]);
        return { tasks, crews };
      },
      [siteId],
    ),
  );
  const selected = resource.data?.tasks.find((t) => t.id === chosen);
  const editingTask = editing ?? resource.data?.tasks.find((t) => t.id === params.get("uredi")) ?? null;
  const closeEditor = () => { setEditing(null); if (params.has("uredi")) { const next = new URLSearchParams(params); next.delete("uredi"); setParams(next, { replace: true }); } };
  return (
    <section className="space-y-4">
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Učitavanje taktova…</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          Brigada
          <select
            className={controlClass}
            value={crew}
            onChange={(e) => setCrew(e.target.value)}
          >
            <option value="">Sve brigade</option>
            {resource.data?.crews.map((c) => (
              <option key={c.id} value={c.id}>
                {c.naziv}
              </option>
            ))}
          </select>
        </label>
        <label>
          Status takta
          <select
            className={controlClass}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="aktivan">Aktivan</option>
            <option value="zavrsen">Završen</option>
            <option value="otkazan">Otkazan</option>
            <option value="">Svi</option>
          </select>
        </label>
      </div>
      {hasPermission(user, "takt", "create") && (
        <Button
          className="min-h-[52px] w-full sm:w-auto"
          onClick={() => setEditing("new")}
        >
          Novi takt
        </Button>
      )}
      {resource.data &&
        editingTask &&
        hasPermission(
          user,
          "takt",
          editingTask === "new" ? "create" : "update",
        ) && (
          <TaskEditor
            key={editingTask === "new" ? "new" : editingTask.id}
            siteId={siteId}
            task={editingTask === "new" ? null : editingTask}
            crews={resource.data.crews}
            onClose={closeEditor}
            onSaved={() => {
              closeEditor();
              resource.reload();
            }}
          />
        )}
      {selected && (
        <TaskDay
          key={selected.id}
          siteId={siteId}
          task={selected}
          initialDate={params.get("datum")}
          onClose={() => setChosen("")}
        />
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {resource.data?.tasks
          .filter(
            (t) =>
              (!crew || t.brigada_id === crew) &&
              (!status || t.status === status),
          )
          .map((task) => (
            <article className={cardClass} key={task.id}>
              <h2 className="text-xl font-bold">{task.naziv}</h2>
              <p className="whitespace-pre-wrap break-words">{task.opis}</p>
              <p>
                {resource.data!.crews.find((c) => c.id === task.brigada_id)
                  ?.naziv ?? "Brigada"}{" "}
                · {task.planirani_od} – {task.planirani_do}
              </p>
              <p>
                {task.status === "aktivan"
                  ? "Aktivan"
                  : task.status === "zavrsen"
                    ? "Završen"
                    : "Otkazan"}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  className="min-h-[52px]"
                  onClick={() => setChosen(task.id)}
                >
                  Dnevni status · {task.naziv}
                </Button>
                {hasPermission(user, "takt", "update") && (
                  <Button
                    className="min-h-[52px]"
                    variant="outline"
                    onClick={() => setEditing(task)}
                  >
                    Uredi takt · {task.naziv}
                  </Button>
                )}
              </div>
            </article>
          ))}
      </div>
      {resource.data?.tasks.filter(
        (t) =>
          (!crew || t.brigada_id === crew) && (!status || t.status === status),
      ).length === 0 && <p>Nema taktova za odabrane filtre.</p>}
    </section>
  );
}

export function TaskEditor({
  siteId,
  task,
  crews,
  onClose,
  onSaved,
  children,
  onSave,
  submitLabel = "Spremi takt",
  initialCrewId = "",
}: {
  siteId: string;
  task: SiteTask | null;
  crews: Crew[];
  onClose: () => void;
  onSaved: () => void;
  children?: ReactNode;
  onSave?: (form: TaskInput) => Promise<void>;
  submitLabel?: string;
  initialCrewId?: string;
}) {
  const [form, setForm] = useState<TaskInput>(
    task
      ? {
          brigada_id: task.brigada_id,
          naziv: task.naziv,
          opis: task.opis,
          planirani_sati: task.planirani_sati ?? null,
          planirani_od: task.planirani_od,
          planirani_do: task.planirani_do,
          status: task.status,
        }
      : {
          brigada_id: initialCrewId,
          naziv: "",
          opis: "",
            planirani_sati: null,
          planirani_od: danas(),
          planirani_do: danas(),
          status: "aktivan",
        },
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start =
    task && task.planirani_od < form.planirani_od
      ? task.planirani_od
      : form.planirani_od;
  const end =
    task && task.planirani_do > form.planirani_do
      ? task.planirani_do
      : form.planirani_do;
  const window = useWriteWindow(siteId, start, end);
  const finishing =
    !!task && form.status === "zavrsen" && task.status === "aktivan";
  const blocked = finishing ? window.periodBlocked : window.blocked;
  const update = <K extends keyof TaskInput>(key: K, value: TaskInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));
  const save = async () => {
    if (blocked || !form.naziv.trim() || form.planirani_do < form.planirani_od)
      return;
    setBusy(true);
    setError(null);
    try {
      if (onSave) await onSave(form);
      else if (task) await workflow.updateTask(task.id, form);
      else await workflow.createTask(siteId, form);
      onSaved();
    } catch (error) {
      setError(errorText(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className={cardClass}
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <h2 className="text-xl font-bold">{task ? "Uredi takt" : "Novi takt"}</h2>
      {children}
      <PeriodLockNotice state={window.state} />
      {window.closed && !finishing && (
        <p role="alert">
          Raspon uključuje zaključen dan; izmjena nije dopuštena.
        </p>
      )}
      {finishing && (
        <p>
          Završetak zahtijeva zaključene sve planirane dane. Pri završetku
          ostali podaci ostaju nepromijenjeni.
        </p>
      )}
      <Feedback error={error ?? window.error} />
      <fieldset disabled={finishing || busy} className="space-y-3">
        <label className="block">
          Planirani sati
          <input className={controlClass} type="number" inputMode="decimal" min="0" max="999999.99" step="0.01" value={form.planirani_sati ?? ""} onChange={(event) => update("planirani_sati", event.target.value || null)} />
          <span className="text-sm">Nije obavezno.</span>
        </label>
        <label className="block">
          Naziv takta
          <input
            className={controlClass}
            required
            maxLength={160}
            value={form.naziv}
            onChange={(e) => update("naziv", e.target.value)}
          />
        </label>
        <label className="block">
          Opis
          <textarea
            className={controlClass}
            maxLength={5000}
            value={form.opis}
            onChange={(e) => update("opis", e.target.value)}
          />
        </label>
        <label className="block">
          Dodijeljena brigada
          <select
            className={controlClass}
            required
            value={form.brigada_id}
            onChange={(e) => update("brigada_id", e.target.value)}
          >
            <option value="">Odaberite brigadu</option>
            {crews
              .filter((c) => c.aktivna || c.id === form.brigada_id)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.naziv}
                </option>
              ))}
          </select>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label>
            Planirano od
            <input
              className={controlClass}
              type="date"
              required
              value={form.planirani_od}
              onChange={(e) => update("planirani_od", e.target.value)}
            />
          </label>
          <label>
            Planirano do
            <input
              className={controlClass}
              type="date"
              min={form.planirani_od}
              required
              value={form.planirani_do}
              onChange={(e) => update("planirani_do", e.target.value)}
            />
          </label>
        </div>
      </fieldset>
      {task && (
        <label className="block">
          Status
          <select
            className={controlClass}
            value={form.status}
            onChange={(e) => {
              const status = e.target.value as TaskInput["status"];
              if (status === "zavrsen")
                setForm({
                  brigada_id: task.brigada_id,
                  naziv: task.naziv,
                  opis: task.opis,
                  planirani_sati: task.planirani_sati ?? null,
                  planirani_od: task.planirani_od,
                  planirani_do: task.planirani_do,
                  status,
                });
              else update("status", status);
            }}
          >
            <option value="aktivan">Aktivan</option>
            <option value="zavrsen">Završen</option>
            <option value="otkazan">Otkazan</option>
          </select>
        </label>
      )}
      <div className="flex flex-wrap gap-3">
        <Button
          className="min-h-[52px]"
          disabled={busy || blocked || !form.naziv.trim() || !form.brigada_id}
        >
          {submitLabel}
        </Button>
        <Button
          className="min-h-[52px]"
          type="button"
          variant="outline"
          disabled={busy}
          onClick={onClose}
        >
          Odustani
        </Button>
      </div>
    </form>
  );
}

function TaskDay({
  siteId,
  task,
  initialDate,
  onClose,
}: {
  siteId: string;
  task: SiteTask;
  initialDate: string | null;
  onClose: () => void;
}) {
  const current = initialDate ?? danas();
  const [date, setDate] = useState(
    current < task.planirani_od
      ? task.planirani_od
      : current > task.planirani_do
        ? task.planirani_do
        : current,
  );
  const [revision, setRevision] = useState(0);
  const history = useResource(
    `history:${task.id}:${revision}`,
    useCallback((signal) => workflow.outcomes(task.id, signal), [task.id]),
  );
  const selected = history.data?.find((row) => row.datum === date);
  return (
    <section className={cardClass}>
      <h2 className="text-xl font-bold">Dnevni status · {task.naziv}</h2>
      <label className="block">
        Datum statusa
        <input
          className={controlClass}
          type="date"
          required
          min={task.planirani_od}
          max={task.planirani_do}
          value={date}
          onChange={(e) => {
            if (e.target.value) setDate(e.target.value);
          }}
        />
      </label>
      <OutcomeLoader
        key={date}
        siteId={siteId}
        task={task}
        date={date}
        locked={selected?.zakljucen ? selected : undefined}
        onSaved={() => setRevision((r) => r + 1)}
      />
      <Button variant="outline" className="min-h-[52px]" onClick={onClose}>
        Zatvori dnevni status
      </Button>
      <OutcomeHistory
        siteId={siteId}
        rows={history.data}
        loading={history.loading}
        error={history.error}
        selectedDate={date}
        onSelect={setDate}
      />
    </section>
  );
}
function changeRequestLink(siteId: string, row: TaskOutcomeSummary): string {
  const query = new URLSearchParams({
    dan: row.dan_id ?? "",
    datum: row.datum,
    gradiliste: siteId,
  });
  return `/zahtjevi-izmjene?${query.toString()}`;
}
function OutcomeHistory({
  siteId,
  rows,
  loading,
  error,
  selectedDate,
  onSelect,
}: {
  siteId: string;
  rows?: TaskOutcomeSummary[];
  loading: boolean;
  error?: string;
  selectedDate: string;
  onSelect: (date: string) => void;
}) {
  const { user } = useAuth();
  const canRequest = hasPermission(user, "zahtjev_izmjene", "create");
  return (
    <div className="space-y-2">
      <h3 className="text-lg font-bold">Predani statusi</h3>
      <Feedback error={error} />
      {loading && !rows && <p role="status">Učitavanje predanih statusa…</p>}
      {rows && !rows.length && <p>Još nema predanih statusa.</p>}
      <ul className="space-y-2">
        {rows?.map((row) => (
          <li
            key={row.id}
            className={`rounded-lg border p-3 space-y-2 ${row.datum === selectedDate ? "border-2 border-[var(--color-foreground)]" : "border-[var(--color-border)]"}`}
          >
            <button
              type="button"
              className="block min-h-[52px] w-full text-left"
              aria-current={row.datum === selectedDate ? "true" : undefined}
              onClick={() => onSelect(row.datum)}
            >
              <span className="block font-semibold">
                {row.datum} · {outcomeLabels[row.status]}
              </span>
              <span className="block text-sm">
                {row.zakljucen ? "Zaključeno" : "Može se mijenjati"} ·
                Fotografija: {row.broj_fotografija}
              </span>
              {row.komentar && (
                <span className="block truncate text-sm">{row.komentar}</span>
              )}
            </button>
            {row.zakljucen && row.dan_id && canRequest && (
              <Link
                className="inline-flex min-h-[44px] items-center underline"
                to={changeRequestLink(siteId, row)}
              >
                Zatraži izmjenu · {row.datum}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
function OutcomeLoader({
  siteId,
  task,
  date,
  locked,
  onSaved,
}: {
  siteId: string;
  task: SiteTask;
  date: string;
  locked?: TaskOutcomeSummary;
  onSaved?: () => void;
}) {
  const { user } = useAuth();
  const result = useResource(
    `${task.id}:${date}`,
    useCallback(
      (signal) => workflow.outcome(task.id, date, signal),
      [task.id, date],
    ),
  );
  return (
    <>
      <Feedback error={result.error} />
      {result.loading ? (
        <p role="status">Učitavanje dnevnog statusa…</p>
      ) : (
        result.data !== undefined &&
        (hasPermission(user, "takt", "update") ? (
          <OutcomeEditor
            siteId={siteId}
            task={task}
            date={date}
            initial={result.data}
            locked={locked}
            onSaved={onSaved}
          />
        ) : (
          <div className="space-y-4">
            {result.data ? (
              <>
                <p>{outcomeLabels[result.data.status]}</p>
                {result.data.komentar && (
                  <p className="whitespace-pre-wrap">{result.data.komentar}</p>
                )}
                {result.data.fotografije.map((id) => (
                  <Photo
                    key={id}
                    taskId={task.id}
                    date={date}
                    documentId={id}
                  />
                ))}
              </>
            ) : (
              <p>Dnevni status još nije unesen.</p>
            )}
          </div>
        ))
      )}
    </>
  );
}
export function Photo({
  file,
  taskId,
  date,
  documentId,
}: {
  file?: File;
  taskId: string;
  date: string;
  documentId?: string;
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    const source = file
      ? Promise.resolve(file)
      : workflow.photo(taskId, date, documentId!, controller.signal);
    void source
      .then((blob) => {
        if (!controller.signal.aborted) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, taskId, date, documentId]);
  return error ? (
    <p>Fotografija nije dostupna.</p>
  ) : url ? (
    <img
      src={url}
      alt={
        file
          ? `Pregled prije slanja: ${file.name}`
          : "Fotografija dnevnog statusa"
      }
      className="max-h-64 w-full rounded-lg object-contain"
    />
  ) : (
    <p>Učitavanje fotografije…</p>
  );
}
export function OutcomeEditor({
  siteId,
  task,
  date,
  initial,
  locked,
  onSaved,
}: {
  siteId: string;
  task: SiteTask;
  date: string;
  initial: TaskOutcome | null;
  locked?: TaskOutcomeSummary;
  onSaved?: () => void;
}) {
  const { user } = useAuth();
  const [status, setStatus] = useState<OutcomeStatus>(
    initial?.status ?? "ostvaren",
  );
  const [comment, setComment] = useState(initial?.komentar ?? "");
  const [saved, setSaved] = useState(initial);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const window = useWriteWindow(siteId, date, date);
  // The day list is date-limited for managers; the task history is not.
  const closed = window.closed || !!locked;
  const writable =
    hasPermission(user, "takt", "update") &&
    task.status === "aktivan" &&
    date >= task.planirani_od &&
    date <= task.planirani_do;
  const missingComment = needsComment(status) && !comment.trim();
  const missingPhoto =
    needsPhoto(status) && !files.length && !saved?.fotografije.length;
  const save = async () => {
    if (!writable || closed || window.blocked || missingComment || missingPhoto)
      return;
    setBusy(true);
    setError(null);
    setMessage(null);
    let statusSaved = false;
    try {
      const outcome = await workflow.saveOutcome(
        task.id,
        date,
        status,
        comment,
      );
      setSaved(outcome);
      statusSaved = true;
      for (const file of files) {
        const photo = await workflow.uploadPhoto(task.id, date, file);
        setSaved((previous) =>
          previous
            ? {
                ...previous,
                fotografije: [...previous.fotografije, photo.dokument_id],
              }
            : previous,
        );
        setFiles((previous) => previous.filter((item) => item !== file));
      }
      setMessage("Dnevni status i odabrane fotografije su spremljeni.");
      window.reload();
      onSaved?.();
    } catch (error) {
      if (statusSaved) onSaved?.();
      setError(
        `${statusSaved ? "Status je spremljen; slanje fotografija nije dovršeno. Preostale fotografije ostaju za ponovni pokušaj. " : ""}${errorText(error)}`,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <PeriodLockNotice state={window.state} />
      {closed && (
        <p role="alert">
          Dan je zaključen. Za promjenu koristite zahtjev izmjene.
          {locked?.dan_id &&
            hasPermission(user, "zahtjev_izmjene", "create") && (
              <>
                {" "}
                <Link
                  className="inline-flex min-h-[44px] items-center underline"
                  to={changeRequestLink(siteId, locked)}
                >
                  Zatraži izmjenu
                </Link>
              </>
            )}
        </p>
      )}
      <Feedback error={error ?? window.error} message={message} />
      <fieldset
        className="space-y-4"
        disabled={!writable || closed || window.blocked || busy}
      >
        <label className="block">
          Dnevni status
          <select
            className={controlClass}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as OutcomeStatus);
              setMessage(null);
            }}
          >
            {Object.entries(outcomeLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          Komentar ({needsComment(status) ? "obvezan" : "opcionalan"})
          <textarea
            className={controlClass}
            required={needsComment(status)}
            maxLength={5000}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </label>
        <label className="block">
          Fotografije (
          {needsPhoto(status) ? "obvezna barem jedna" : "opcionalno"})
          <input
            className={controlClass}
            type="file"
            accept="image/jpeg,image/png"
            capture="environment"
            multiple
            onChange={(e) => {
              const chosen = Array.from(e.target.files ?? []);
              if (
                chosen.some(
                  (f) => !["image/jpeg", "image/png"].includes(f.type),
                )
              )
                setError("Odaberite JPEG ili PNG fotografiju.");
              else {
                setFiles((previous) => [...previous, ...chosen]);
                setError(null);
              }
              e.target.value = "";
            }}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}:${file.lastModified}:${index}`}
              className="space-y-2"
            >
              <Photo file={file} taskId={task.id} date={date} />
              <Button
                type="button"
                variant="outline"
                className="min-h-[52px]"
                onClick={() =>
                  setFiles((previous) => previous.filter((_, i) => i !== index))
                }
              >
                Ukloni nespremljenu fotografiju {index + 1}
              </Button>
            </div>
          ))}
        </div>
        {missingComment && <p>Za odabrani status unesite komentar.</p>}
        {missingPhoto && (
          <p>Za odabrani status dodajte barem jednu fotografiju.</p>
        )}
        <Button
          className="min-h-[52px] w-full"
          disabled={missingComment || missingPhoto}
        >
          Spremi dnevni status
        </Button>
      </fieldset>
      <div className="grid gap-3 sm:grid-cols-2">
        {saved?.fotografije.map((id) => (
          <Photo key={id} taskId={task.id} date={date} documentId={id} />
        ))}
      </div>
    </form>
  );
}
